/**
 * Storico modifiche (log audit) di un Ticket, in fondo alla riga espansa: pannello chiuso di
 * default, eventi letti solo all'apertura, visibile con VIEW_AUDIT_LOG.
 *
 * product-service audita le mutazioni del ticket come `product_triage` con entityId = id del
 * ticket. I ticket automatici nascono (e ricevono le nuove CVE) dall'upsert per release, auditato
 * con entityId = subjectRef (`product-triage:<release>`): quella serie è condivisa con eventuali
 * ticket precedenti della stessa release, quindi si leggono solo gli eventi dalla creazione in poi.
 */

import { AuditTrail } from '../audit/AuditTrail.js';
import type { AuditEventRow, AuditHistoryQuery } from '../audit/audit-api.js';
import { triageKeys } from './hooks.js';
import type { Ticket } from './types.js';

/** Margine sull'istante di creazione: l'evento di creazione è registrato dopo l'insert. */
const MARGINE_CREAZIONE_MS = 60_000;

export const TICKET_AUDIT_FIELD_LABEL: Record<string, string> = {
  status: 'Stato',
  triageLead: 'In carico a',
  analystNotes: 'Note di analisi',
  addedCollaborator: 'Collaboratore aggiunto',
  removedCollaborator: 'Collaboratore rimosso',
  closedReason: 'Motivo chiusura',
  closedAt: 'Chiuso il',
  reopenReason: 'Motivo riapertura',
  cve: 'CVE',
  assignedCve: 'CVE assegnata',
  analysisState: 'Stato VEX',
  category: 'Categoria ENISA',
  reportIds: 'Report ENISA',
  role: 'Ruolo',
  ticketId: 'Ticket',
};

/** Serie audit del ticket: per id e, per i ticket automatici, per subjectRef dalla creazione. */
export function ticketAuditQueries(
  ticket: Pick<Ticket, 'id' | 'origin' | 'subjectRef' | 'createdAt'>,
): AuditHistoryQuery[] {
  const queries: AuditHistoryQuery[] = [
    { entityTypes: ['product_triage'], entityId: String(ticket.id) },
  ];
  if (ticket.origin === 'auto' && ticket.subjectRef) {
    const creato = ticket.createdAt ? Date.parse(ticket.createdAt) : NaN;
    queries.push({
      entityTypes: ['product_triage'],
      entityId: ticket.subjectRef,
      ...(Number.isFinite(creato)
        ? { from: new Date(creato - MARGINE_CREAZIONE_MS).toISOString() }
        : {}),
    });
  }
  return queries;
}

function descriviEvento(ev: AuditEventRow): string | undefined {
  if (ev.entityId.startsWith('product-triage:')) return 'aggiornamento automatico (snapshot)';
  return undefined;
}

export function TicketAuditTrail({ ticket }: { ticket: Ticket }) {
  return (
    <AuditTrail
      queryKey={[...triageKeys.ticket(ticket.id), 'audit']}
      queries={ticketAuditQueries(ticket)}
      testId={`storico-ticket-${ticket.id}`}
      describe={descriviEvento}
      fieldLabel={TICKET_AUDIT_FIELD_LABEL}
    />
  );
}
